import { select, scaleLinear, scaleBand, axisBottom, axisLeft, max } from 'd3';
import {
  splitAndTrim,
  parseValue,
  formatYAxis,
  fetchChartData,
  setupHoverHighlight,
} from './chart-utils';

/**
 * Initializes bar charts on the page
 */
export function initBarChart() {
  const charts = document.querySelectorAll('.bar-chart-wrapper .chart-inner');

  if (charts.length === 0) {
    return;
  }

  for (const chart of charts) {
    if (chart instanceof HTMLElement) {
      renderBarChart(chart);
    }
  }
}

/**
 * Renders a single bar chart
 * @param {HTMLElement} chartElement - The chart container element
 */
function renderBarChart(chartElement) {
  const file = chartElement.getAttribute('data-file');
  const xColumn = chartElement.getAttribute('data-x-column') || 'Date';
  const columns = chartElement.getAttribute('data-columns');
  const colors = chartElement.getAttribute('data-colors');
  const yFormat = chartElement.getAttribute('data-y-format') || 'number';
  const stacked = chartElement.getAttribute('data-stacked') === 'true';

  if (!file) {
    return;
  }

  const columnsArray = splitAndTrim(columns);
  const colorArray = splitAndTrim(colors);

  fetchChartData(chartElement, file, '.bar-chart-wrapper', (data) => {
    renderChart(
      chartElement,
      data,
      xColumn,
      columnsArray,
      colorArray,
      yFormat,
      stacked,
    );
  });
}

/**
 * Renders the chart using D3
 * @param {HTMLElement} container - The chart container
 * @param {Array} data - Parsed CSV data
 * @param {string} xColumn - X column name
 * @param {Array<string>} columns - Bar column names
 * @param {Array<string>} colors - Color array
 * @param {string} yFormat - Y-axis format type
 * @param {boolean} stacked - Whether to stack bars
 */
function renderChart(
  container,
  data,
  xColumn,
  columns,
  colors,
  yFormat,
  stacked,
) {
  container.innerHTML = '';

  const margin = { top: 0, right: 30, bottom: 60, left: 40 };
  const width = container.clientWidth - margin.left - margin.right;
  const height = container.clientHeight - margin.top - margin.bottom;

  const svg = select(container)
    .append('svg')
    .attr('width', width + margin.left + margin.right)
    .attr('height', height + margin.top + margin.bottom)
    .style('opacity', 0)
    .append('g')
    .attr('transform', `translate(${margin.left},${margin.top})`);

  // Fade in the chart
  select(container)
    .select('svg')
    .transition()
    .duration(600)
    .style('opacity', 1);

  // Process data
  const processedData = data.map((d, i) => {
    const dateStr = d[xColumn];
    let year;
    let label;
    let showLabel = false;

    // Parse date/label
    if (dateStr.includes('/')) {
      year = new Date(dateStr).getFullYear();
      label = year;
    } else if (dateStr.match(/Q\d/i)) {
      const parts = dateStr.trim().split(/\s+/);
      year = parseInt(parts[parts.length - 1], 10);
      label = dateStr;
      showLabel = dateStr.match(/Q1/i) !== null;
    } else {
      year = parseInt(dateStr, 10);
      label = year;
      showLabel = i === 0 || year !== parseInt(data[i - 1][xColumn], 10);
    }

    return {
      index: i,
      year,
      label,
      showLabel,
      ...columns.reduce((acc, col) => {
        acc[col] = parseValue(d[col]);
        return acc;
      }, {}),
    };
  });

  // X scale
  const xScale = scaleBand()
    .domain(processedData.map((d) => String(d.index)))
    .range([0, width])
    .padding(0.1);

  // Y scale
  const maxValue = max(processedData, (d) =>
    Math.max(...columns.map((col) => d[col])),
  );
  const yScale = scaleLinear()
    .domain([0, (maxValue || 0) * 1.1])
    .range([height, 0]);

  // X axis
  svg
    .append('g')
    .attr('class', 'x-axis')
    .attr('transform', `translate(0,${height})`)
    .call(
      axisBottom(xScale)
        .tickValues(
          processedData.filter((d) => d.showLabel).map((d) => String(d.index)),
        )
        .tickFormat((i) => {
          const d = processedData.find((item) => String(item.index) === i);
          return d ? d.label : '';
        })
        .tickSize(0)
        .tickPadding(10),
    )
    .call((g) => g.select('.domain').attr('stroke', '#444'))
    .selectAll('text')
    .style('text-anchor', 'end')
    .style('fill', '#1D2328')
    .attr('dx', '-.8em')
    .attr('dy', '.15em')
    .attr('transform', 'rotate(-65)');

  // Y axis
  svg
    .append('g')
    .attr('class', 'y-axis')
    .call(
      axisLeft(yScale)
        .tickFormat((d) => formatYAxis(Number(d), yFormat))
        .tickSize(-width)
        .tickPadding(10),
    )
    .call((g) => g.select('.domain').remove())
    .call((g) => g.selectAll('.tick line').attr('stroke', '#e0e0e0'))
    .call((g) =>
      g.selectAll('text').style('fill', '#999').style('font-size', '14px'),
    );

  // Draw bars
  const chartElements = [];

  if (stacked && columns.length > 1) {
    // Stacked bars - not implemented yet, would need d3.stack
    console.warn('Stacked bars not yet implemented in D3 bar chart');
  } else if (columns.length === 1) {
    // Single series
    const bars = svg
      .selectAll('.bar')
      .data(processedData)
      .enter()
      .append('rect')
      .attr('class', 'bar')
      .attr('data-series', columns[0])
      .attr('x', (d) => xScale(String(d.index)) || 0)
      .attr('y', (d) => yScale(d[columns[0]]))
      .attr('height', (d) => height - yScale(d[columns[0]]))
      .attr('width', xScale.bandwidth())
      .attr('fill', colors[0] || '#264C68')
      .style('cursor', 'pointer');

    chartElements.push({ series: columns[0], element: bars });
  } else {
    // Grouped bars
    const barWidth = xScale.bandwidth() / columns.length;
    for (let i = 0; i < columns.length; i++) {
      const column = columns[i];
      const bars = svg
        .selectAll(`.bar-${i}`)
        .data(processedData)
        .enter()
        .append('rect')
        .attr('class', `bar-${i}`)
        .attr('data-series', column)
        .attr('x', (d) => (xScale(String(d.index)) || 0) + i * barWidth)
        .attr('y', (d) => yScale(d[column]))
        .attr('height', (d) => height - yScale(d[column]))
        .attr('width', barWidth)
        .attr('fill', colors[i] || '#264C68')
        .style('cursor', 'pointer');

      chartElements.push({ series: column, element: bars });
    }
  }

  // Legend (if multiple columns)
  if (columns.length > 1) {
    const legend = svg
      .append('g')
      .attr('class', 'legend')
      .attr('transform', `translate(0,${height + 40})`);

    const legendElements = [];
    let currentX = 0;

    for (let i = 0; i < columns.length; i++) {
      const legendItem = legend
        .append('g')
        .attr('class', 'legend-item')
        .attr('data-series', columns[i])
        .attr('transform', `translate(${currentX},0)`)
        .style('cursor', 'pointer');

      legendItem
        .append('rect')
        .attr('x', 0)
        .attr('y', -6)
        .attr('width', 12)
        .attr('height', 12)
        .attr('fill', colors[i] || '#264C68');

      legendItem
        .append('text')
        .attr('x', 18)
        .attr('y', 0)
        .attr('dy', '0.32em')
        .style('font-size', '11px')
        .style('fill', '#444')
        .style(
          'font-family',
          '"Roboto", "Helvetica Neue", "HelveticaNeue", "Helvetica", "Arial", sans-serif',
        )
        .text(columns[i]);

      legendElements.push({ label: columns[i], element: legendItem });

      // Update position for next item
      const bbox = legendItem.node()?.getBBox();
      if (bbox) {
        currentX += bbox.width + 20;
      }
    }

    // Setup hover highlighting
    setupHoverHighlight(svg, legendElements, chartElements);
  }
}
